import {useMemo, useState} from "react";
import {
    Button,
    Group,
    Modal,
    Radio,
    Stack,
    Tabs,
    TextInput,
} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {notifications} from "@mantine/notifications";
import type {CollectionDataResponse, LabelGroup} from "../../types";
import {dibApi} from "../../../chat/dibApi";
import {buildCollectionUrl, type TopicMeta} from "./utils";
import {OpenFillingModal} from "./OpenFillingModal";

type FillingFormModalProps = {
    opened: boolean;
    onClose: () => void;
    mode: "create" | "edit" | "open";
    topicMeta: TopicMeta;
    defaultCollectionId: string;
    viewGroups: LabelGroup[];
    labelData: Record<string, string>;
    collectionData?: CollectionDataResponse;
    onPatchDimensions: (dimensions: string[]) => Promise<void>;
    onPatchCollectionName: (name: string) => Promise<void>;
    isPatchCollectionNameLoading: boolean;
    isPatchDimensionsLoading: boolean;
    onCollectionChange: () => void;
};

export const FillingFormModal = ({
    opened,
    onClose,
    mode,
    topicMeta,
    defaultCollectionId,
    viewGroups,
    labelData,
    collectionData,
    onPatchDimensions,
    onPatchCollectionName,
    isPatchCollectionNameLoading,
    isPatchDimensionsLoading,
    onCollectionChange,
}: FillingFormModalProps) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const isNew = mode === "create" || !collectionData;

    const defaultTab = mode === "open" ? "open" : "form";

    // Compute initial dimensions from collection labels or labelData defaults
    const initialDimensions = useMemo(() => {
        const dims: Record<string, string> = {};
        const selectedLabelIds =
            collectionData?.labels?.map((l) => l.labelId) ??
            Object.values(labelData);

        for (const group of viewGroups) {
            const match = selectedLabelIds.find((labelId) =>
                group.labels.some((l) => l.id === labelId),
            );
            dims[group.id] = match ?? group.labels[0]?.id ?? "";
        }
        return dims;
    }, [viewGroups, collectionData, labelData]);

    const [name, setName] = useState(isNew ? "" : (collectionData?.metadata?.name ?? ""));
    const [dimensions, setDimensions] = useState(initialDimensions);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [createCollection] =
        dibApi.endpoints.createCollection.useMutation();

    const handleSubmit = async () => {
        if (!name.trim()) return;

        setIsSubmitting(true);

        try {
            if (isNew) {
                // Create new collection
                const changedDims = Object.values(dimensions).filter(
                    (id) => id && !Object.values(initialDimensions).includes(id),
                );

                const result = await createCollection({
                    topicId: topicMeta.topicId,
                    collectionId: defaultCollectionId,
                    name: name.trim(),
                    dimensions: changedDims,
                }).unwrap();

                onCollectionChange();
                navigate(
                    buildCollectionUrl(topicMeta, {
                        name: result.name,
                        id: result.collectionId.toLowerCase(),
                    }),
                );
                onClose();
            } else {
                // Edit existing collection
                const changedDims = Object.values(dimensions).filter(
                    (id) =>
                        id && !Object.values(initialDimensions).includes(id),
                );

                if (name.trim() !== collectionData?.metadata?.name) {
                    await onPatchCollectionName(name.trim());
                }
                if (changedDims.length > 0) {
                    await onPatchDimensions(changedDims);
                }

                onClose();
            }
        } catch {
            notifications.show({
                message: t("Error.SomethingWentWrong"),
                color: "red",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const loading =
        isSubmitting ||
        isPatchCollectionNameLoading ||
        isPatchDimensionsLoading;

    const editCreateLabel = isNew ? t("Word.New") : t("Word.Edit");

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t("Word.Filling")}
            size="md"
        >
            <Tabs defaultValue={defaultTab}>
                <Tabs.List>
                    <Tabs.Tab value="form">{editCreateLabel}</Tabs.Tab>
                    <Tabs.Tab value="open">{t("Word.Open")}</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="form" pt="md">
                    <Stack>
                        <TextInput
                            label={isNew ? t("Action.NameFilling") : t("Word.Name")}
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            autoFocus
                        />

                        {viewGroups.map((group) => (
                            <Radio.Group
                                key={group.id}
                                label={group.name}
                                value={dimensions[group.id]}
                                onChange={(value) =>
                                    setDimensions((prev) => ({
                                        ...prev,
                                        [group.id]: value,
                                    }))
                                }
                            >
                                <Stack gap="xs" mt="xs">
                                    {group.labels.map((label) => (
                                        <Radio
                                            key={label.id}
                                            value={label.id}
                                            label={label.name}
                                        />
                                    ))}
                                </Stack>
                            </Radio.Group>
                        ))}

                        <Group justify="flex-end">
                            <Button
                                variant="subtle"
                                onClick={onClose}
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button
                                loading={loading}
                                onClick={handleSubmit}
                            >
                                {isNew ? t("Word.create") : t("Action.Save")}
                            </Button>
                        </Group>
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="open" pt="md">
                    <OpenFillingModal
                        onClose={onClose}
                        onCollectionChange={onCollectionChange}
                        topicId={topicMeta.topicId}
                        topicMeta={topicMeta}
                    />
                </Tabs.Panel>
            </Tabs>
        </Modal>
    );
};
